<template>
  <NormalHeaderLayout
    header="Help"
    :isWithoutBackground="true"
    :class="{ mobile: isMobile }"
  >
    <!-- <Accordion :title="'About DASH'">
      <template slot="body">
        <div :style="{ padding: '1rem 0' }">
          It is for network operators who want to ensure their network is
          secure, and stay on top of security news.
        </div>
      </template>
    </Accordion> -->
    <div class="section">
      <h2>Overview</h2>
      <p>
        <strong>DASH</strong> is the <strong>D</strong>ashboard for
        <strong>A</strong>utonomous <strong>S</strong>ystem
        <strong>H</strong>ealth, a system designed to inform Internet Number
        Resource (INR) holders about the status of their resources in the global
        internet.
      </p>
      <p>
        DASH consists of two subsidiary services: routing status and suspicious
        traffic. Each one collects data from different sources, and reports on
        different aspects of the INR behaviour seen in the global internet.
      </p>
    </div>

    <div class="section">
      <h2>Components of the DASH webpage</h2>
      <div class="components">
        <div class="description">
          <p>
            <strong> The DASH webpage has 3 principal components </strong>
          </p>
          <ul>
            <li>
              <strong>Top bar </strong>
              <span class="sub-title">
                <span class="sub-title__dash">- </span>Account and asset
                selection
              </span>
            </li>
            <li>
              <strong>Sidebar </strong>
              <span class="sub-title">
                <span class="sub-title__dash">- </span>Left side steerage and
                sub-selection
              </span>
            </li>
            <li>
              <strong>Main body </strong>
              <span class="sub-title">
                <span class="sub-title__dash">- </span>Service summary and
                Tabular view
              </span>
            </li>
          </ul>
        </div>
        <div class="illustration">
          <img src="/static/help/components.svg" alt="components of DASH" />
        </div>
      </div>

      <div class="accordion-container">
        <Accordion
          :title="
            isMobile ? 'Top bar' : 'Top bar - Account and asset selection'
          "
        >
          <template slot="body">
            <div :style="{ padding: '1rem 0' }">
              <p>
                APNIC supports logins via single-sign-on (SSO) which have access
                to multiple APNIC accounts. Therefore, DASH allows you to select
                which APNIC account is to be shown.
              </p>
              <p>
                For each account, two primary asset classes can be selected:
              </p>
              <ul>
                <li>
                  <strong> All your prefixes - "your prefixes" </strong>
                  <br />
                  <span class="li-indent--20px">
                    Available in routing status only.
                  </span>
                </li>
                <li>
                  <strong>Specific Origin-AS</strong>
                  <br />
                  <span class="li-indent--20px">
                    Available in both routing status and suspicious traffic.
                  </span>
                </li>
              </ul>
              <div v-if="!isMobile" class="img-container">
                <img
                  src="/static/help/top-bar_desktop.png"
                  alt="top bar of DASH"
                  class="with-border"
                />
              </div>
              <div v-else class="img-container">
                <img
                  src="/static/help/top-bar_mobile.png"
                  alt="top bar of DASH"
                  class="with-border"
                />
              </div>
            </div>
          </template>
        </Accordion>
      </div>

      <div class="accordion-container">
        <Accordion
          :title="
            isMobile
              ? 'Sidebar'
              : 'Sidebar - Left side steerage and sub-selection'
          "
        >
          <template slot="body">
            <div :style="{ padding: '1rem 0' }" class="content-container">
              <div class="left">
                <p>
                  Each subsidiary service can be selected. By default, DASH
                  shows the routing status pages.
                </p>
                <p>
                  For each subsidiary service, two sub-contexts can be chosen:
                </p>
                <ul>
                  <li>
                    <span>
                      <strong> Dashboard </strong>
                      - which summarizes the current status of this service
                      overall.
                    </span>
                  </li>
                  <li>
                    <span>
                      <strong>Alerts</strong>
                      - which shows all configured alerts for that service, and
                      their status.
                    </span>
                  </li>
                </ul>
                <p>
                  Additionally in suspicious traffic, you can select “request
                  insights” from APNIC security staff.
                </p>
                <p>
                  Left side steerage includes background information about the
                  DASH services, our data sources and the service disclaimers as
                  well as this help text.
                </p>
              </div>
              <div class="right">
                <img
                  src="/static/help/sidebar.png"
                  alt="sidebar of DASH"
                  class="with-border"
                />
              </div>
            </div>
          </template>
        </Accordion>
      </div>

      <div class="accordion-container">
        <Accordion
          :title="
            isMobile
              ? 'Main body'
              : 'Main body - Service summary and tabular view'
          "
        >
          <template slot="body">
            <div :style="{ padding: '1rem 0' }" class="main-body">
              <div class="left">
                <p>
                  The main body provides service-specific information, in two
                  basic forms:
                </p>

                <ul>
                  <li>
                    <span>
                      <strong> Service summary </strong>
                      - a brief outline of the condition of this view of your
                      INR with any problems highlighted (such as alerts which
                      are active)
                    </span>
                    <ul :style="{ 'margin-left': '1rem' }">
                      <li :style="{ 'list-style-type': 'circle' }">
                        For suspicious traffic this includes comparisons to the
                        relevant economy and region, and a chart view of the
                        traffic.
                      </li>
                    </ul>
                  </li>
                  <li>
                    <span>
                      <strong>Tabular view</strong>
                      - a complete listing of all prefixes and their status.
                    </span>
                  </li>
                </ul>
              </div>
              <div v-if="!isMobile" class="img-container">
                <img
                  src="/static/help/main-body_desktop.png"
                  alt="main body of DASH"
                />
              </div>
              <div v-else class="img-container">
                <img
                  src="/static/help/main-body_mobile.png"
                  alt="main body of DASH"
                />
              </div>
            </div>
          </template>
        </Accordion>
      </div>
    </div>

    <div class="section">
      <h2>
        DASH's subsidiary services:<br v-if="isMobile" />
        routing status & suspicious traffic
      </h2>

      <div class="accordion-container">
        <Accordion :title="'Routing status overview'">
          <template slot="body">
            <div :style="{ padding: '1rem 0' }">
              <p>
                The DASH routing status service compares two different views of
                declared routing intent with BGP. All three sources of
                information are then assessed against each other, and any
                differences are flagged.
              </p>
              <p>
                <strong> What this page displays: </strong>
              </p>
              <ul>
                <li>Status of the BGP announcements for each prefix.</li>
                <li>
                  Inconsistencies among your BGP announcements, RPKI ROAs and
                  IRR Route Objects.
                </li>
              </ul>
              <p>
                Currently, APNIC only reports on the state of IRR against the
                following IRR sources: APNIC, LACNIC, ARIN, RIPE, AFRINIC,
                JPIRR, JPNIC, KRNIC and TWNIC.
              </p>
              <p>
                For all prefixes seen in BGP, the system analyses this BGP
                against RPKI ROA and IRR route object status.
              </p>
              <p>
                <strong>The status can be:</strong>
              </p>

              <ul :style="{ 'list-style-type': 'none' }">
                <li>
                  <span class="prefix-icon green-dot"> Published </span>
                  <br />
                  <span class="li-indent--20px">
                    A record of this type (IRR or RPKI ROA) does not conflict
                    with BGP.
                  </span>
                </li>
                <li>
                  <span class="prefix-icon yellow-dot"> Not published </span>
                  <br />
                  <span class="li-indent--20px">
                    No record of this type (IRR or RPKI ROA) applies to this BGP
                    announcement.
                  </span>
                </li>
                <li>
                  <span class="prefix-icon red-dot"> Mismatch </span>
                  <br />
                  <span class="li-indent--20px">
                    A published record of this type (IRR or RPKI ROA) conflicts
                    with this route seen in BGP. If your prefix has this label,
                    click on its '+info' link for more details.
                  </span>
                </li>
              </ul>

              <a
                href="/routing-status"
                @click="navigateTo($event, '/routing-status')"
                >Go to routing status</a
              >

              <div v-if="!isMobile" class="img-container">
                <img
                  src="/static/help/main-body_desktop.png"
                  alt="routing status of DASH"
                />
              </div>
              <div v-else class="img-container">
                <img
                  src="/static/help/main-body_mobile.png"
                  alt="routing status of DASH"
                />
              </div>
            </div>
          </template>
        </Accordion>
      </div>

      <div class="accordion-container">
        <Accordion :title="'Suspicious traffic overview'">
          <template slot="body">
            <div :style="{ padding: '1rem 0' }">
              <p>
                The DASH suspicious traffic service collects data from a
                distributed honeypot network that is part of APNIC's Community
                Honeynet Project. This shows visible threats originating from
                inside a given AS for both the INR held by that delegate, and
                any other addresses which it originates.
              </p>
              <p>
                Suspicious traffic is also shown for the economy the AS is
                registered in, and for the region that economy lies in. This
                provides a basic “how do I compare against other people” view,
                for your economy and region.
              </p>

              <a
                href="/routing-status"
                @click="navigateTo($event, '/suspicious-traffic')"
              >
                Go to suspicious traffic
              </a>

              <div v-if="!isMobile" class="img-container">
                <img
                  src="/static/help/suspicious-traffic_desktop.png"
                  alt="suspicious traffic of DASH"
                />
              </div>
              <div v-else class="img-container">
                <img
                  src="/static/help/suspicious-traffic_mobile.png"
                  alt="suspicious traffic of DASH"
                />
              </div>
            </div>
          </template>
        </Accordion>
      </div>
    </div>

    <div class="section">
      <h2>Configuring and using alerts</h2>
      <p>
        As well as providing on-screen displays of status, both services support
        the use of an alerting mechanism.
      </p>
      <p>
        You can define which networks or subnetworks or ASN you want to be
        informed about, and trigger conditions. APNIC will send you an alert
        (when the conditions re met) over SMS, email or slack as you define.
      </p>

      <div class="accordion-container">
        <Accordion :title="'Adding an alert'">
          <template slot="body">
            <div :style="{ padding: '1rem 0' }">
              <p>
                To add an alert, click the "New alert" button to commence a
                4-part wizard.
              </p>
              <div v-if="!isMobile" class="img-container">
                <img
                  src="/static/help/create-alert_desktop.png"
                  alt="create alert in DASH"
                  class="with-border"
                />
              </div>
              <div v-else class="img-container">
                <img
                  src="/static/help/create-alert_mobile.png"
                  alt="create alert in DASH"
                />
              </div>

              <p>
                <strong>
                  An alert consists of the following 4 components:
                </strong>
              </p>
              <ol>
                <li>
                  <strong>A trigger</strong>
                  - which INR are to be considered
                </li>
                <li>
                  <strong>A rule</strong>
                  - what specific conditions are to be met for the selected INR
                </li>
                <li>
                  <strong>An action</strong>
                  - what to do, when the conditions are met (how we send you the
                  alert)
                </li>
                <li>
                  <strong>A name</strong>
                  - how you see the alert in the tabular view and in alert
                  messages
                </li>
              </ol>
              <p>
                Triggers are slightly different between routing status and
                suspicious traffic alerts. In routing status alerts, you can
                select specific prefixes or origin AS, in suspicious traffic
                alerts the context is a specific origin AS so only the routed
                prefixes can be selected.
              </p>
              <p>
                Alerts can be snoozed (disabled) and re-enabled when snoozed,
                edited (which returns you to the wizard) or deleted.
              </p>
              <p>
                "Firing" alerts can be paused (snoozed) and re-enabled through
                the link contained in the alert message as well as from the web.
              </p>
              <p>
                <strong> Alert status indicators </strong>
              </p>
              <p>DASH alert status indicators and their descriptions:</p>
              <ul :style="{ 'list-style-type': 'none' }">
                <li>
                  <span class="prefix-icon green-dot"> Clean </span>
                  <br />
                  <span class="li-indent--20px">
                    The alert has never been triggered.
                  </span>
                </li>
                <li>
                  <span class="prefix-icon red-circle"> Firing </span>
                  <br />
                  <span class="li-indent--20px">
                    The alert has been triggered.
                  </span>
                </li>
                <li>
                  <span class="prefix-icon red-dot"> Fired </span>
                  <br />
                  <span class="li-indent--20px">
                    The alert has previously been triggered but has since been
                    resolved.
                  </span>
                </li>
                <li>
                  <span class="prefix-icon yellow-dot"> Snoozed </span>
                  <br />
                  <span class="li-indent--20px">
                    Notifications for the alert have been disabled.
                  </span>
                </li>
              </ul>

              <div class="link-container">
                <a
                  href="/alerts-routing-status"
                  @click="navigateTo($event, '/alerts-routing-status')"
                  >Go to routing status alerts
                </a>
                <a
                  href="/alerts-suspicious-traffic"
                  @click="navigateTo($event, '/alerts-suspicious-traffic')"
                  >Go to suspicious traffic alerts
                </a>
              </div>
            </div>
          </template>
        </Accordion>
      </div>

      <div class="accordion-container">
        <Accordion :title="'How you receive the alerts'">
          <template slot="body">
            <div :style="{ padding: '1rem 0' }">
              <p>Currently, APNIC can send alerts by SMS, Email or Slack.</p>
              <p>
                SMS and Email are straightforward to use, and we support any
                existing known email addresses or phone numbers as targets. To
                maintain who can receive these alerts you will need to use your
                APNIC account management to set permitted contacts, if for
                example you wish to include a NOC or other queue address.
              </p>
              <p>
                For Slack, you will need to use the APNIC Notifications Slack
                application. Instructions on how to install it can be found
                <a
                  href="https://help.apnic.net/s/article/slack-notifications"
                  target="_blank"
                >
                  here
                  <fa icon="external-link-alt" /> </a
                >.
              </p>
            </div>
          </template>
        </Accordion>
      </div>

      <!-- It's been removed temporarily as the "Request insights" tab has been hidden. -->
      <!-- <div class="accordion-container">
        <Accordion
          :title="
            isMobile
              ? 'Request insights'
              : 'Request insights (suspicious traffic only)'
          "
        >
          <template slot="body">
            <div :style="{ padding: '1rem 0' }">
              <p v-if="isMobile">Suspicious traffic only.</p>
              <p>
                If you want to speak to APNIC security specialists about your
                suspicious traffic flows, this service will send a request with
                the details of interest.
              </p>
              <p>
                This is not a time-bound service and is offered as best-effort
                subject to availability of staff to respond.
              </p>
              <a href="/what-to-do" @click="navigateTo($event, '/what-to-do')"
                >Go to request insights
              </a>

              <div v-if="!isMobile" class="img-container">
                <img
                  src="/static/help/request-insights_desktop.png"
                  alt="request insights in DASH"
                />
              </div>
              <div v-else class="img-container">
                <img
                  src="/static/help/request-insights_mobile.png"
                  alt="request insights in DASH"
                  class="with-border"
                />
              </div>
            </div>
          </template>
        </Accordion>
      </div> -->
    </div>
  </NormalHeaderLayout>
</template>

<script>
import { mapGetters } from 'vuex';
import NormalHeaderLayout from '../components/layout/NormalHeaderLayout';
import Accordion from '../components/ui/Accordion.vue';

export default {
  name: 'Help',
  components: {
    NormalHeaderLayout,
    Accordion
  },
  computed: {
    ...mapGetters(['isMobile'])
  },
  methods: {
    navigateTo(e, path) {
      // It's important to prevent default behaviour of a link, otherwise it will refresh the page when navigating.
      e.preventDefault();

      this.$router.push(path);
    }
  }
};
</script>

<style scoped lang="scss">
h2 {
  margin-bottom: 1.5rem;
}

.components {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-evenly;
  align-items: center;
  margin-bottom: 1rem;

  .description {
    flex: 0 0 auto;
    max-width: 27rem;
  }

  .sub-title {
    display: initial;
  }

  .illustration {
    flex: 0 0 18rem;

    img {
      width: 100%;
    }
  }
}

.section {
  margin-bottom: 2.5rem;
}

.section ul {
  list-style-type: disc;
  list-style-position: inside;
  margin-left: 0.25rem;

  li {
    line-height: 1.5rem;
    margin-bottom: 0.5rem;
  }
  .li-indent--20px {
    display: inline-block;
    margin-left: 1.25rem;
  }
  .li-indent--16px {
    display: inline-block;
    margin-left: 1rem;
  }
}

.accordion-container {
  margin: 0.5rem 0;
}

.img-container {
  width: 100%;
  padding: 0 10%;
  margin: 1.5rem 0 0.5rem 0;

  img {
    width: 100%;
  }
}

img.with-border {
  border: 1px solid $text-black;
}

.content-container {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  .left {
    flex: 0 0 60%;
    min-width: 20rem;
  }

  .right {
    text-align: center;
    flex: 0 0 40%;

    img {
      width: 100%;
    }
  }
}

@media screen and (max-width: 992px) {
  .content-container {
    flex-direction: column;
  }
}

.main-body {
  .left {
    padding-bottom: 0.25rem;
  }
}

a {
  display: inline-block;
  margin-bottom: 1rem;
  color: $text-light-blue;
  text-decoration: none;
}

ol {
  list-style-position: inside;
}

.link-container {
  a:nth-of-type(1) {
    padding-right: 3rem;
  }
}

.mobile {
  h2 {
    font-size: 1.25rem;
    margin-bottom: 1rem;
  }

  .components {
    .description {
      flex: auto;
    }

    .sub-title {
      display: block;
      margin-left: 1.25rem;

      &__dash {
        display: none;
      }
    }

    .illustration {
      flex: 0 1 90%;
    }
  }

  .content-container {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .img-container {
    padding: 0;
  }
}
</style>
